// Phase 12.7 — Email + password sign-in. Lives at /login.
//
// Two-column layout on >=md: Google OAuth left, email+password right.
// Honours the same `scope:post_login` sessionStorage flag the Google flow
// uses so post-auth routing stays consistent.
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { useAuth } from "../lib/AuthContext";
import { safeNextPath } from "../lib/safeNextPath";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Login() {
    const { user, signIn, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [retryAfter, setRetryAfter] = useState(0);
    const [publicReg, setPublicReg] = useState(false);

    const nextHref = useMemo(() => {
        const qs = new URLSearchParams(location.search);
        const next = qs.get("next") || sessionStorage.getItem("scope:post_login") || "/analyses";
        // Issue #35 — `next.startsWith("/")` also passed a protocol-relative
        // value like `//evil.com` (browsers treat a leading `//` as
        // same-scheme-different-host). Shared validator closes that gap
        // and keeps this in sync with AuthCallback.jsx's own check.
        return safeNextPath(next, "/analyses");
    }, [location.search]);

    useEffect(() => {
        if (user) navigate(nextHref, { replace: true });
    }, [user, nextHref, navigate]);

    useEffect(() => {
        // Discover whether public registration is enabled
        fetch(`${API}/api/auth/registration-open`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : { enabled: false })
            .then((d) => setPublicReg(!!d.enabled))
            .catch(() => setPublicReg(false));
    }, []);

    useEffect(() => {
        if (retryAfter <= 0) return;
        const t = setInterval(() => setRetryAfter((v) => Math.max(0, v - 1)), 1000);
        return () => clearInterval(t);
    }, [retryAfter]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting || retryAfter > 0) return;
        setErr(null);
        // Basic email format check
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            setErr("Enter a valid email address.");
            return;
        }
        if (!password) {
            setErr("Enter your password.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
            });
            if (r.status === 429) {
                const ra = parseInt(r.headers.get("Retry-After") || "60", 10);
                setRetryAfter(Number.isFinite(ra) ? ra : 60);
                setErr(`Too many attempts. Try again in ${ra}s.`);
                return;
            }
            if (!r.ok) {
                const b = await r.json().catch(() => ({}));
                setErr(formatApiErrorDetail(b.detail) || "Invalid email or password");
                return;
            }
            // 200 — refresh AuthContext (sets user) then navigate
            await refresh();
            navigate(nextHref, { replace: true });
        } catch (e2) {
            setErr(e2.message || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="page-login">
            <Nav />
            <div className="pt-28 pb-24">
                <div className="max-w-3xl mx-auto px-6 sm:px-8">
                    <div className="eyebrow mb-3">Sign in</div>
                    <h1 className="font-serif text-[34px] sm:text-[42px] leading-[1.06] tracking-tight font-medium text-ink mb-3">
                        Welcome back to Tomauro
                    </h1>
                    <p className="text-[14px] text-ink-soft mb-10 max-w-xl leading-relaxed">
                        Pick the method that works for you. Both surface the same account.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 items-stretch">
                        {/* ---- Google ---- */}
                        <div className="bg-paper-soft/60 border border-ink/12 p-6 flex flex-col">
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-3">
                                Single sign-on
                            </div>
                            <h2 className="font-serif text-[20px] text-ink mb-2">Continue with Google</h2>
                            <p className="text-[13px] text-ink-soft leading-relaxed mb-6 flex-1">
                                Use your Google account. We never see your password, and your
                                browser handles the redirect.
                            </p>
                            <button
                                data-testid="login-google-button"
                                onClick={() => {
                                    try {
                                        sessionStorage.setItem("scope:post_login", nextHref);
                                    } catch (_) { /* ignore */ }
                                    signIn();
                                }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-ink text-paper text-[13px] tracking-wide hover:bg-amber-accent transition-colors"
                            >
                                <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden>
                                    <path fill="currentColor" d="M44.5 20H24v8.5h11.8C34.7 33 30 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l6-6C34.5 5.8 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11.1 0 20-8.9 20-20 0-1.4-.1-2.7-.5-4z" />
                                </svg>
                                Sign in with Google
                            </button>
                        </div>

                        {/* ---- Divider ---- */}
                        <div className="hidden md:flex flex-col items-center justify-center">
                            <div className="w-px flex-1 bg-ink/10" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft py-2">or</span>
                            <div className="w-px flex-1 bg-ink/10" />
                        </div>

                        {/* ---- Email + password ---- */}
                        <form
                            onSubmit={handleSubmit}
                            className="bg-paper border border-ink/12 p-6 flex flex-col"
                            data-testid="login-password-form"
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-3">
                                Email + password
                            </div>
                            <h2 className="font-serif text-[20px] text-ink mb-4">Sign in</h2>
                            <label htmlFor="login-email" className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft mb-1">
                                Email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={submitting}
                                autoFocus
                                autoComplete="email"
                                data-testid="login-email-input"
                                className="bg-paper border border-ink/15 px-3 py-2 text-[14px] text-ink focus:outline-none focus:border-ink/45 transition-colors mb-3"
                            />
                            <label htmlFor="login-password" className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft mb-1">
                                Password
                            </label>
                            <div className="relative mb-4">
                                <input
                                    id="login-password"
                                    type={showPwd ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={submitting}
                                    autoComplete="current-password"
                                    data-testid="login-password-input"
                                    className="w-full bg-paper border border-ink/15 px-3 py-2 pr-10 text-[14px] text-ink focus:outline-none focus:border-ink/45 transition-colors"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    data-testid="login-password-toggle"
                                    aria-label={showPwd ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-7 h-7 items-center justify-center text-ink-soft hover:text-ink"
                                >
                                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                            </div>
                            {err && (
                                <div
                                    data-testid="login-error"
                                    className="mb-3 flex items-start gap-2 px-3 py-2 border border-[#9B3E3E]/40 bg-[#9B3E3E]/8 text-[12px] text-[#9B3E3E]"
                                >
                                    <AlertCircle size={13} className="mt-0.5 flex-none" />
                                    <span>{err}</span>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={submitting || retryAfter > 0}
                                data-testid="login-submit"
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-ink text-paper text-[13px] tracking-wide hover:bg-amber-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {submitting && <Loader2 size={14} className="animate-spin" />}
                                {retryAfter > 0 ? `Wait ${retryAfter}s…` : (submitting ? "Signing in…" : "Sign in")}
                            </button>
                            <div className="mt-4 text-[12px] text-ink-soft">
                                {publicReg ? (
                                    <Link data-testid="login-register-link" to="/register" className="hover:text-amber-accent transition-colors">
                                        Create account →
                                    </Link>
                                ) : (
                                    <a data-testid="login-request-access-link" href="mailto:research@coda.app?subject=Request%20access" className="hover:text-amber-accent transition-colors">
                                        Don't have an account? Request access →
                                    </a>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
